import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Drawer,
} from '@mui/material';
import {
  Send as SendIcon,
  Help as HelpIcon,  
  Edit as EditIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';

const LoadingDots = () => (
  <Box 
    sx={{ 
      display: 'flex',
      gap: 1,
      p: 1,
      '& .dot': {
        width: 8,
        height: 8,
        backgroundColor: 'grey.500',
        borderRadius: '50%',
        animation: 'dotPulse 1.5s infinite',
      },
      '& .dot:nth-of-type(2)': {
        animationDelay: '0.2s',
      },
      '& .dot:nth-of-type(3)': {
        animationDelay: '0.4s',
      },
      '@keyframes dotPulse': {
        '0%, 100%': {
          transform: 'scale(0.7)',
          opacity: 0.5,
        },
        '50%': {
          transform: 'scale(1)',
          opacity: 1,
        },
      },
    }}
  >
    <Box className="dot" />
    <Box className="dot" />
    <Box className="dot" />
  </Box>
);

const Chat = () => {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stationId, setStationId] = useState('RHI');
  const [lakeType, setLakeType] = useState('Traditional Deep & Clear');
  const [messages, setMessages] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [jsonData, setJsonData] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationLabel, setRecommendationLabel] = useState("Recommendations");
  const [helpEnabled, setHelpEnabled] = useState(false);

  const fetchLatestActivity = async (stationId) => {
    try {
      const currentUtc = new Date().toISOString();
      const docRef = doc(db, 'activities', stationId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        // Find the most recent activity
        const activities = data.activities || [];
        const latestActivity = activities
          .filter(activity => activity.dateTime <= currentUtc)
          .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))[0];

        if (latestActivity) {
          const newMetadata = {
            lake: "Minocqua",
            lakeType: lakeType,
            localTimeZone: "America/Chicago",
            stationId: stationId,
            timeOfYear: data.timeOfYear.name || 'Normal',
            biometricsLater: latestActivity.later,
            biometricsLaterDateTime: latestActivity.laterDateTime,
            biometricsNext: latestActivity.next,
            biometricsNextDateTime: latestActivity.nextDateTime,
            biometricsNow: latestActivity.now,
            biometricsNowDateTime: latestActivity.nowDateTime,
            biometricsPattern: latestActivity.biometrics.pattern,
            biometricsScore: latestActivity.score,
            fishingDate: new Date(latestActivity.dateTime).toLocaleString(),
            moonPercent: `0%`,
            moonPhase: 'Full Moon',
            moonOverhead: new Date(latestActivity.dateTime).toISOString(),
            moonRise: new Date(latestActivity.dateTime).toISOString(),
            moonSet: new Date(latestActivity.dateTime).toISOString(),
            moonUnderfoot: new Date(latestActivity.dateTime).toISOString(),
            displayName: 'Admin Portal Tester'
          };
          setMetadata(newMetadata);
        } else {
          console.error('No valid activity found for date:', currentUtc);
          setMetadata(null);
        }
      } else {
        console.error('No document found for station:', stationId);
        setMetadata(null);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  // Load initial data when component mounts
  useEffect(() => {
    if (stationId) {
      fetchLatestActivity(stationId);
    }
  }, []); // Empty dependency array means this runs once on mount

  const handleStationIdBlur = () => {
    if (stationId) {
      fetchLatestActivity(stationId);
    }
  };

  const getRequestData = (customMessage) => {
    if (!metadata) return null;
    
    const data = {
      userId: currentUser.uid,
      message: customMessage?.trim() || message.trim(),
      source: "admin-portal",
      metadata: {
        ...metadata,
        uid: currentUser.uid,
        stationId: stationId,
        lakeType: lakeType
      }
    };
    return data;
  };

  useEffect(() => {
    if (drawerOpen) {
      setJsonData(JSON.stringify(getRequestData(), null, 2));
    }
  }, [drawerOpen, message, stationId, lakeType]);

  const handleSend = async (customMessage) => {
    const messageText = customMessage || message;
    if ((messageText?.trim()) && currentUser && metadata) {
      const newMessage = {
        text: messageText,
        sender: 'user',
        timestamp: new Date().toISOString(),
      };
      if (messageText === 'startover') {
        setMessages([]);
      } else {
        setMessages([...messages, newMessage]);
      }
      setMessage(''); // Clear input field immediately after adding message

      let requestData;
      try {
        setIsLoading(true);
        requestData = drawerOpen ? JSON.parse(jsonData) : getRequestData(messageText);
        const token = await currentUser.getIdToken();
        const response = await fetch('https://us-central1-bsi-bass.cloudfunctions.net/mhtvChatBot/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        
        // Add bot response to messages and check for recommendations
        if (data.message) {
          const botMessage = {
            text: data.message,
            sender: 'bot',
            timestamp: new Date().toISOString(),
          };
          setMessages(msgs => [...msgs, botMessage]);
          
          // Check for recommendations in the response data
          if (data.recommendations && data.recommendations.length > 0) {
            setRecommendations(data.recommendations);
            setRecommendationLabel(data.recommendationLabel || "Recommendations");
            setHelpEnabled(true);
          } else {
            setRecommendations([]);
            setRecommendationLabel("Recommendations");
            setHelpEnabled(false);
          }
        }
      } catch (error) {
        console.error('Error sending message:', error);
        setMessages(msgs => [...msgs, {
          text: 'Error: Failed to send message',
          sender: 'bot',
          timestamp: new Date().toISOString(),
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleHelp = () => {
    setShowRecommendations(!showRecommendations);
  };

  const handleRecommendationClick = async (recommendation) => {
    setShowRecommendations(false);
    // Submit the message
    await handleSend(recommendation);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Header controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5.75}>
            <TextField
              fullWidth
              label="Station ID"
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
              onBlur={handleStationIdBlur}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={5.75}>
            <FormControl fullWidth>
              <InputLabel>Lake Type</InputLabel>
              <Select
                value={lakeType}
                label="Lake Type"
                onChange={(e) => setLakeType(e.target.value)}
              >
                <MenuItem value="Traditional Shallow & Stained">Traditional Shallow & Stained</MenuItem>
                <MenuItem value="Traditional Deep & Clear">Traditional Deep & Clear</MenuItem>
                <MenuItem value="Canadian Shield Shallow Stained">Canadian Shield Shallow Stained</MenuItem>
                <MenuItem value="Canadian Trout Water">Canadian Trout Water</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={0.5} sx={{ display: 'flex', justifyContent: 'flex-end', marginLeft: 'auto' }}>
            <IconButton
              color="primary"
              onClick={() => handleSend("startover")}
              sx={{ width: 'auto' }}
            >
              <RestartAltIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Chat messages */}
        <Paper
          sx={{
            flex: 1,
            mb: 2,
            p: 2,
            overflow: 'auto',
            bgcolor: 'grey.50',
          }}
        >
        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
              mb: 1,
            }}
          >
            <Paper
              sx={{
                p: 1,
                maxWidth: '70%',
                bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
                color: msg.sender === 'user' ? 'white' : 'text.primary',
              }}
            >
              {msg.sender === 'user' ? (
                <Typography variant="body1">{msg.text}</Typography>
              ) : (
                <Box sx={{ '& p': { m: 0 } }}>
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </Box>
              )}
            </Paper>
          </Box>
        ))}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 1,
            }}
          >
            <Paper
              sx={{
                bgcolor: 'white',
              }}
            >
              <LoadingDots />
            </Paper>
          </Box>
        )}
        </Paper>



      {/* Recommendations panel */}
      {showRecommendations && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 115,
            mb: 1,
            p: 2,
            maxWidth: '100%',
            zIndex: 1,
            bgcolor: 'background.paper',
            boxShadow: 3,
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            {recommendationLabel}
          </Typography>
          {recommendations.length > 0 ? recommendations.map((rec, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{
                cursor: 'pointer',
                p: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              onClick={() => handleRecommendationClick(rec)}
            >
              {rec}
            </Typography>
          )) : (
            <Typography variant="body2" sx={{ p: 1 }}>
              No recommendations available at this time
            </Typography>
          )}
        </Paper>
      )}
      
      {/* JSON Editor Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '40%',
            minWidth: '300px',
            p: 2,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Edit Request JSON</Typography>
          <TextField
            fullWidth
            multiline
            value={jsonData}
            onChange={(e) => setJsonData(e.target.value)}
            variant="outlined"
            sx={{ 
              flex: 1,
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Drawer>

      {/* Input area */}
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          color="primary"
          onClick={() => setDrawerOpen(true)}
          sx={{ order: 1, width: 'auto' }}
        >
          <EditIcon />
        </IconButton>
        <IconButton 
          color="primary"
          onClick={handleHelp}
          sx={{ 
            order: 2, 
            width: 'auto'
          }}
        >
          <HelpIcon />
        </IconButton>
        <TextField
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message..."
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSend();
            }
          }}
          sx={{ order: 2 }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!message.trim() || !metadata}
          sx={{ order: 3, width: 'auto' }}
        >
          <SendIcon />
        </IconButton>
      </Paper>
    </Box>
  );
};

export default Chat;
